/**
 * Asset Management API client (ADR-0024). Thin wrappers over /v1/asset-categories,
 * /v1/assets, /v1/asset-assignments, /v1/asset-imports, /v1/me/assets, and
 * /v1/employees/:id/assets — mirroring api/expense.ts.
 */
import { apiRequest, apiDownload } from './client.js';

export type AssetStatus = 'available' | 'retired' | 'lost';
export type DerivedAssetStatus = 'available' | 'assigned' | 'retired' | 'lost';
export type AssignmentStatus = 'requested' | 'approved' | 'issued' | 'returned' | 'rejected' | 'cancelled';
export type AssignmentOrigin = 'state_machine' | 'import';

export interface AssetCategory {
  id: string;
  key: string;
  label: string;
  active: boolean;
}
export interface Asset {
  id: string;
  assetTag: string;
  name: string;
  serial: string | null;
  categoryId: string;
  status: DerivedAssetStatus; // derived (D5) — never inferred client-side
  openAssignmentId: string | null; // the open (issued) assignment, when assigned — lets the catalog offer Return
  purchaseCost: number | null;
  currency: string | null;
  purchaseDate: string | null;
}
export interface Assignment {
  id: string;
  status: AssignmentStatus;
  origin?: AssignmentOrigin;
  employeeId: string;
  assetId: string | null;
  categoryId: string;
  approverId: string | null;
  assignedAt: string | null;
  returnedAt: string | null;
  notes?: string | null;
  employee?: { firstName: string; lastName: string; employeeNumber: string } | null;
  asset?: { assetTag: string; name: string } | null;
  category?: { key: string; label: string } | null;
}
/** A holding as returned by /me/assets + /employees/:id/assets. */
export interface Holding {
  id: string;
  status: AssignmentStatus;
  current: boolean;
  assetId: string | null;
  assetTag: string | null;
  name: string | null;
  categoryId: string;
  assignedAt: string | null;
  returnedAt: string | null;
}

// ── Categories ───────────────────────────────────────────────────────────────
export function listCategories(): Promise<{ categories: AssetCategory[] }> {
  return apiRequest('/v1/asset-categories');
}
export function createCategory(body: { key: string; label: string }): Promise<AssetCategory> {
  return apiRequest('/v1/asset-categories', { method: 'POST', body });
}
export function updateCategory(id: string, body: { label?: string; active?: boolean }): Promise<AssetCategory> {
  return apiRequest(`/v1/asset-categories/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export function deleteCategory(id: string): Promise<{ ok: true }> {
  return apiRequest(`/v1/asset-categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Catalog ──────────────────────────────────────────────────────────────────
export function listAssets(params: { categoryId?: string; status?: string } = {}): Promise<{ assets: Asset[] }> {
  const q = new URLSearchParams();
  if (params.categoryId) q.set('categoryId', params.categoryId);
  if (params.status) q.set('status', params.status);
  const qs = q.toString();
  return apiRequest(`/v1/assets${qs ? `?${qs}` : ''}`);
}
export function createAsset(body: { assetTag: string; name: string; categoryId: string; serial?: string; purchaseCost?: number; currency?: string; purchaseDate?: string }): Promise<Asset> {
  return apiRequest('/v1/assets', { method: 'POST', body });
}
export function updateAsset(id: string, body: { name?: string; serial?: string | null; status?: AssetStatus }): Promise<Asset> {
  return apiRequest(`/v1/assets/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}

// ── Custody lifecycle ────────────────────────────────────────────────────────
export function listAssignments(params: { status?: string } = {}): Promise<{ assignments: Assignment[] }> {
  const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
  return apiRequest(`/v1/asset-assignments${qs}`);
}
export function listApprovals(view: 'pending_my_approval' | 'all'): Promise<{ assignments: Assignment[] }> {
  return apiRequest(`/v1/asset-assignments/approvals?view=${view}`);
}
/** employeeId optional (#275): an employee omits it to request for themselves;
 *  HR supplies it to request on someone's behalf. */
export function requestAsset(body: { employeeId?: string; categoryId: string; notes?: string }): Promise<Assignment> {
  return apiRequest('/v1/asset-assignments', { method: 'POST', body });
}
/** Cancel a pre-issue requisition (#275 E2 — an employee may cancel their own). */
export function cancelRequest(id: string): Promise<Assignment> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} });
}

/** ADR-0025 — the custodian's at-a-glance aggregate (the IT dashboard). Counts
 *  only; no employee/HR data. */
export interface AssetDashboardData {
  queue: { awaitingIssue: number; pendingApproval: number };
  catalog: { available: number; assigned: number; retired: number; lost: number; total: number };
  byCategory: Array<{ key: string; label: string; available: number; assigned: number }>;
}
export function getAssetDashboard(): Promise<AssetDashboardData> {
  return apiRequest('/v1/assets/dashboard');
}
export function approveRequest(id: string, body: { reason?: string } = {}): Promise<Assignment> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(id)}/approve`, { method: 'POST', body });
}
export function rejectRequest(id: string, body: { reason?: string } = {}): Promise<Assignment> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(id)}/reject`, { method: 'POST', body });
}
/** D2: issuing is the CUSTODIAN control — distinct from approve; binds a serial. */
export function issueAsset(id: string, body: { assetId: string; conditionOut?: string }): Promise<Assignment> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(id)}/issue`, { method: 'POST', body });
}
export function returnAsset(id: string, body: { conditionIn?: string } = {}): Promise<Assignment> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(id)}/return`, { method: 'POST', body });
}
/** Transfer is keyed by the ASSET (POST /v1/assets/:id/transfer), not the assignment. */
export function transferAsset(assetId: string, body: { toEmployeeId: string; conditionIn?: string; conditionOut?: string; notes?: string }): Promise<Assignment> {
  return apiRequest(`/v1/assets/${encodeURIComponent(assetId)}/transfer`, { method: 'POST', body });
}

// ── Read views ───────────────────────────────────────────────────────────────
export function listMyAssets(): Promise<{ assets: Holding[] }> {
  return apiRequest('/v1/me/assets');
}
export function listEmployeeAssets(employeeId: string): Promise<{ employeeId: string; assets: Holding[] }> {
  return apiRequest(`/v1/employees/${encodeURIComponent(employeeId)}/assets`);
}

/** ADR-0025 D6 — the identity-only employee roster (name/number/dept/position/
 *  manager/status). The asset pickers use THIS, not the HR-grade employee list,
 *  so the `it` custodian role can populate them without HR-record access. */
export interface RosterEmployee { id: string; firstName: string; lastName: string; employeeNumber: string; department: string | null; position: string | null; status: string; manager: string | null }
export function listRoster(params: { status?: string } = {}): Promise<{ employees: RosterEmployee[] }> {
  const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
  return apiRequest(`/v1/employees/roster${qs}`);
}

// ── Attachments (handover docs) ──────────────────────────────────────────────
export const HANDOVER_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
export const HANDOVER_ALLOWED_LABEL = 'PDF, Word (.doc/.docx), or image (JPG/PNG)';
export interface HandoverDoc { id: string; filename: string; contentType: string; sizeBytes: number; label: string | null; uploadedAt: string }
export function listAttachments(assignmentId: string): Promise<{ attachments: HandoverDoc[] }> {
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(assignmentId)}/attachments`);
}
export function uploadAttachment(assignmentId: string, file: File, label?: string): Promise<HandoverDoc> {
  const form = new FormData();
  form.append('file', file);
  if (label) form.append('label', label);
  return apiRequest(`/v1/asset-assignments/${encodeURIComponent(assignmentId)}/attachments`, { method: 'POST', body: form });
}
export function downloadAttachment(assignmentId: string, artifactId: string, filename: string): Promise<void> {
  return apiDownload(`/v1/asset-assignments/${encodeURIComponent(assignmentId)}/attachments/${encodeURIComponent(artifactId)}/download`, filename);
}

// ── CSV import ───────────────────────────────────────────────────────────────
export type ImportKind = 'category' | 'asset';
export interface ImportRowError { row: number; category: string; message: string }
export interface ImportRecord {
  id: string;
  importKind: ImportKind;
  status: string;
  rowCount: number;
  validRowCount: number;
  errorRowCount: number;
  warningRowCount: number;
  errorsByCategory: Record<string, number>;
  errors: ImportRowError[];
  committedCount: number;
}
export function downloadImportTemplate(kind: ImportKind): Promise<void> {
  return apiDownload(`/v1/asset-imports/template?kind=${kind}`, `lumohr-asset-${kind}-template.csv`);
}
export function uploadImport(kind: ImportKind, file: File): Promise<ImportRecord> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  return apiRequest('/v1/asset-imports', { method: 'POST', body: form });
}
export function commitImport(id: string): Promise<{ id: string; status: string; committedCount: number }> {
  return apiRequest(`/v1/asset-imports/${encodeURIComponent(id)}/commit`, { method: 'POST', body: {} });
}
export function cancelImport(id: string): Promise<{ id: string; status: string }> {
  return apiRequest(`/v1/asset-imports/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} });
}

/** Columns the category CSV expects — surfaced inline in the import UI. */
export const CATEGORY_IMPORT_COLUMNS: Array<{ name: string; required: string; note: string }> = [
  { name: 'key', required: 'always', note: 'Lowercase slug (a-z, 0-9, _) — no spaces or capitals. The internal handle, e.g. laptop, pos_terminal.' },
  { name: 'label', required: 'always', note: 'The human name shown in the app, e.g. Laptops. A broad bucket, not a single item.' },
];

/** Columns the asset CSV expects — surfaced inline in the import UI (2b). */
export const ASSET_IMPORT_COLUMNS: Array<{ name: string; required: string; note: string }> = [
  { name: 'asset_tag', required: 'always', note: 'Case-sensitive. New tag → create; existing tag + a holder → assign.' },
  { name: 'name', required: 'new assets', note: 'Required when creating a new asset; ignored when assigning an existing one.' },
  { name: 'category_key', required: 'new assets', note: 'Must match an active category key (case-insensitive).' },
  { name: 'serial', required: 'optional', note: '' },
  { name: 'status', required: 'optional', note: 'Only "available" (or blank).' },
  { name: 'purchase_cost', required: 'optional', note: 'Number ≥ 0.' },
  { name: 'currency', required: 'optional', note: '3-letter ISO.' },
  { name: 'purchase_date', required: 'optional', note: 'YYYY-MM-DD.' },
  { name: 'assigned_to_employee_number', required: 'optional', note: 'Exact, case-sensitive. Present → assign the asset to this holder.' },
  { name: 'assigned_at', required: 'optional', note: 'YYYY-MM-DD. Blank = date unknown — never today’s date.' },
];
